import { useCallback, useMemo } from "react";
import type { FormStore } from "../../store/types";
import type { FieldId, FieldValue, FieldValueType } from "../../types";

interface Params {
  store: FormStore;
  validateField: (fieldId: FieldId) => Promise<boolean>;
  validateAll: () => Promise<boolean>;
}

export function useFormValues({ store, validateField, validateAll }: Params) {
  // 値を設定
  const setValue = useCallback(
    (fieldId: FieldId, value: FieldValueType, validate = true) => {
      store.setValue(fieldId, value);
      if (validate) {
        void validateField(fieldId);
      }
    },
    [store, validateField],
  );

  // 複数の値を設定
  const setValues = useCallback(
    (values: FieldValue[], validate = true) => {
      store.setValues(values);
      if (validate) {
        void validateAll();
      }
    },
    [store, validateAll],
  );

  // 値のmemoized変換
  const values = useMemo(() => {
    // 値をフィールドIDをキーとした連想配列形式に変換
    return store.values.reduce(
      (acc, field) => {
        acc[field.fieldId] = field.value;
        return acc;
      },
      {} as Record<FieldId, FieldValueType>,
    );
  }, [store.values]);

  return {
    setValue,
    setValues,
    values,
  };
}
